/**
 * Iter143 — UID-Duplikats-Check-Banner.
 *
 * Prüft beim Setzen einer UID (aus Zefix oder händisch), ob die Firma bereits
 * im System registriert ist. Zeigt:
 *  - Grüne Banner "Du hast bereits Zugriff → jetzt öffnen" wenn User Zugriff hat
 *  - Gelbe Warnung "Firma existiert bereits (Owner: m***t@…) — Anmelden statt registrieren"
 *  - Nichts wenn keine Duplikat
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Building2, CheckCircle2, ArrowRight } from "lucide-react";

export default function UidDuplicateBanner({ uid }) {
  const navigate = useNavigate();
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const digits = (uid || "").replace(/\D/g, "");
    if (digits.length !== 9) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiClient.get(`/companies/uid-check?uid=${encodeURIComponent(uid)}`);
        if (!cancelled) setCheck(r.data);
      } catch {
        if (!cancelled) setCheck(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [uid]);

  if (!check || !check.exists) return null;

  if (check.has_access) {
    return (
      <div
        className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-3"
        data-testid="uid-dup-banner-access"
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-emerald-900">
            Du hast bereits Zugriff auf <strong>{check.name}</strong> ({check.role})
          </div>
          <div className="text-xs text-emerald-800 mt-0.5">
            Diese Firma ist bereits in deinem Portfolio. Kein erneutes Anlegen nötig.
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 h-7 text-xs bg-white"
            onClick={() => navigate(`/companies/${check.company_id}`)}
            data-testid="uid-dup-open-btn"
          >
            <ArrowRight className="h-3 w-3 mr-1" />
            Zur Firma
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-3"
      data-testid="uid-dup-banner-warning"
    >
      <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-amber-900">
          <Building2 className="h-4 w-4 inline mr-1 -mt-0.5" />
          Firma bereits registriert: <strong>{check.name}</strong>
        </div>
        <div className="text-xs text-amber-800 mt-1 leading-relaxed">
          Diese UID gehört bereits zu einer Firma im System
          {check.owner_email_masked && (
            <> (Owner: <span className="font-mono">{check.owner_email_masked}</span>)</>
          )}.
          Bitte den Owner um Freigabe deiner E-Mail-Adresse — dann kannst du dich
          per Einmal-Code anmelden statt eine Duplikat-Firma anzulegen.
        </div>
      </div>
    </div>
  );
}
